///
/// EarthBrowser
///
/// Created by Matt Giger on 9/14/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Global radar mosaic dataset layer. Syncs the `radar` Arrow dataset
/// (5-minute global equirect frames composited from the tier-1 national
/// radar networks by radar-services) and animates it through the same
/// RadarTextureFrame / TextureSeries path as MRMS and IMERG. Has its own
/// "radar" visibility toggle; when IMERG ("precipitation") is also on,
/// ground radar echoes draw per-pixel on top of the satellite estimate.
///
/// Each record carries two times: slot_timestamp is the server's build
/// slot (keys the refinement tile archive), data_timestamp is the newest
/// observation in the composite. Frames are placed on the timeline by
/// data_timestamp so the mosaic lines up with the other time layers.
///

import { type Database } from "@/earthbrowser/database/Database"
import { type Earth } from "@/earthbrowser/activities/EarthActivity/Earth"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { RadarOverlay } from "@/earthbrowser/dataset-distribution/radar/RadarOverlay"
import { type LayerLoadState } from "@/earthbrowser/datasets/LoadState"
import { type TimeLaneDetail } from "@/earthbrowser/datasets/TimeLane"
import { RadarTextureFrame } from "@/earthbrowser/dataset-distribution/mrms/RadarTextureFrame"
import { engineBridge, isPaidEntitlement } from "@/earthbrowser/engineBridge"
import { isMobile } from "@/earthbrowser/utils/platform"
import { time, timedelta } from "@stovokor/math/time"
import { TileFrame } from "@/earthbrowser/datasets/TileSeries"
import { DatasetLayer, type DatasetDelta, type DatasetManifest, type DatasetSceneObject, type DatasetSyncConfig } from "../DatasetLayer"
import { RadarTable, type RadarFrameRecord } from "./RadarTable"

// Frames are 5-minute slots. The 48-hour window is the paid "time machine"
// entitlement (SUBSCRIPTION_PRICING_MODEL.md Phase 4): free users get the
// 2-hour loop everywhere, paid users get 48 hours on desktop. Mobile stays
// at 2 hours regardless of tier to limit sync and image-cache churn.
// GPU memory stays bounded either way (RadarTextureSeries purges frames
// outside the preload window).
const cPaidDesktopFrameLimit = 576
const cFreeFrameLimit = 24

/** Effective frame limit: min(tier limit, device cap) */
function frameLimit(): number {
	if (isMobile()) return cFreeFrameLimit
	// No store (embed builds) means free-tier caps
	return isPaidEntitlement() ? cPaidDesktopFrameLimit : cFreeFrameLimit
}

// Zoom-in refinement tiles: each mosaic slot has a matching immutable
// PMTiles archive on the tile server, addressed as
// /radar/{slot ISO}/{z}/{x}/{y}.webp
const cTileServerUrl = import.meta.env.VITE_TILE_SERVER_URL || "https://tiles.earthbrowser.com"

/** Raw Arrow row shape for the radar mosaic frames feed (only fields we read) */
interface RadarRow {
	id: unknown
	product: unknown
	data_timestamp: unknown
	slot_timestamp: unknown
	image_url: unknown
	source_path: unknown
	size_bytes: unknown
	width: unknown
	height: unknown
	vmin: unknown
	vmax: unknown
	bounds_wgs84: string | null | undefined
	created_at: unknown
}

export class RadarDatasetLayer extends DatasetLayer<RadarFrameRecord> {
	private radarOverlay: RadarOverlay
	private earth: Earth
	// Cache frames by URL to avoid reloading unchanged frames
	private frameCache = new Map<string, RadarTextureFrame>()
	// Redux state subscription unsubscribe function
	private unsubscribe: (() => void) | null = null
	// Last slot set fed to the refinement tile series, to avoid resetting
	// the series (and its current-frame state) when nothing changed
	private lastTileSeriesKey = ""
	// Last seen entitlement tier, to re-query frames when depth changes
	// (e.g. checkout completes and the 48-hour window unlocks live)
	private lastPaidState = false

	constructor(context: EarthBrowserContext, earth: Earth) {
		super(context, "radar")
		this.radarOverlay = new RadarOverlay(context)
		this.earth = earth
		// The mosaic only hands off to the refinement tiles while some are
		// actually drawing, so a missing or slow archive never loses the radar
		this.radarOverlay.refinementRendering = () => {
			const cache = this.earth.cacheTask
			return cache.wmTileSeries.hasFrames && cache.wmRenderingTiles.length > 0
		}
		// The tiles blend the same slot pair as the mosaic (same frame times,
		// same selection rule), so they advect along the mosaic's estimated
		// flow field rather than cross-dissolving once they own the radar
		this.earth.cacheTask.wmTileSeries.flowTexture = () => this.radarOverlay.flowTexture
		// ...and fade out with it when the forecast radar takes over past
		// the newest observation (RadarForecastDatasetLayer)
		this.earth.cacheTask.wmTileSeries.fade = () => 1 - this.radarOverlay.yieldToForecast()

		// Initialize visibility based on current state
		const initialVisible = engineBridge.getState()?.earthbrowser.visibleDatasets.includes("radar") ?? false
		this.visible = initialVisible
		this.lastPaidState = isPaidEntitlement()
		if (initialVisible) {
			this.earth.addOverlay(this.radarOverlay)
		}

		// Subscribe to visibility and entitlement state changes
		this.unsubscribe = engineBridge.subscribe(() => {
			const state = engineBridge.getState()
			if (!state) return
			this.setVisible(state.earthbrowser.visibleDatasets.includes("radar"))
			if (state.entitlements.isPaid !== this.lastPaidState) {
				this.lastPaidState = state.entitlements.isPaid
				this.lastTileSeriesKey = ""
				void this.refresh()
			}
		})
	}

	public getSyncConfig(): DatasetSyncConfig {
		return {
			datasetId: "radar",
			pollIntervalMs: 60_000,
			needsSync: true,
			supportsDelta: true,
		}
	}

	public async createTable(db: Database): Promise<void> {
		await RadarTable.create(db)
	}

	public override getAvailableTimeRange(): { startMs: number; endMs: number } | null {
		const range = this.radarOverlay.getTimeRange()
		return range ? { startMs: range.start.date.getTime(), endMs: range.end.date.getTime() } : null
	}

	/** Observation time (ms) of the newest mosaic frame, for the forecast hand-off */
	public latestObservedMs(): number | undefined {
		return this.radarOverlay.latestFrameMs
	}

	/** Share of the picture ceded to the forecast radar (see RadarOverlay.yieldToForecast) */
	public set yieldToForecast(value: () => number) {
		this.radarOverlay.yieldToForecast = value
	}

	public override getTimeLaneDetail(): TimeLaneDetail | null {
		return this.radarOverlay.getTimeLaneDetail()
	}

	protected override frameLoadState(): LayerLoadState | null {
		return this.radarOverlay.getLoadState()
	}

	public async applyCheckpoint(
		manifest: DatasetManifest,
		downloadArrowRows: (url: string) => Promise<unknown[]>
	): Promise<void> {
		const shard = manifest.checkpoint.shards[0]
		if (!shard) return
		const url = this.resolveUrl(shard.url, shard.path)
		const rows = await downloadArrowRows(url)
		const frames = this.parseRows(rows)
		await RadarTable.replaceAll(frames)
	}

	public async applyDelta(
		delta: DatasetDelta,
		downloadArrowRows: (url: string) => Promise<unknown[]>
	): Promise<void> {
		const upsertsUrl = this.resolveUrl(delta.upserts.url, delta.upserts.path)
		const upsertRows = await downloadArrowRows(upsertsUrl)
		const upserts = this.parseRows(upsertRows)
		if (upserts.length > 0) {
			await RadarTable.upsert(upserts)
		}
	}

	private parseRows(rows: unknown[]): RadarFrameRecord[] {
		return (rows as RadarRow[]).map(r => ({
			id: String(r.id),
			product: String(r.product),
			data_timestamp: String(r.data_timestamp),
			slot_timestamp: String(r.slot_timestamp),
			image_url: String(r.image_url),
			source_path: String(r.source_path),
			size_bytes: Number(r.size_bytes),
			width: Number(r.width),
			height: Number(r.height),
			vmin: Number(r.vmin),
			vmax: Number(r.vmax),
			bounds_wgs84: r.bounds_wgs84 === null || r.bounds_wgs84 === undefined ? null : String(r.bounds_wgs84),
			created_at: String(r.created_at),
		}))
	}

	public async queryFeatures(): Promise<RadarFrameRecord[]> {
		const rows = await RadarTable.getRecent(frameLimit())
		return rows
	}

	public createSceneObject(_feature: RadarFrameRecord): DatasetSceneObject<RadarFrameRecord> | undefined {
		// Radar frames are consumed by overlay rendering, not by creating scene objects.
		return undefined
	}

	public async onSyncComplete(): Promise<void> {
		await this.refresh()
	}

	/**
	 * Refresh radar frames from the database and update the overlay.
	 * Uses delta calculation to reuse existing frames and only load new ones.
	 */
	public override async refresh(): Promise<void> {
		try {
			const records = await RadarTable.getRecent(frameLimit())
			const textureFrames = this.updateFrameCache(records)
			this.radarOverlay.setFrames(textureFrames)
			if (this.visible) {
				this.updateTileSeries(records)
			}
		} catch (error) {
			console.error("[RadarDatasetLayer] Failed to refresh:", error)
		}
	}

	/**
	 * Feed the Web Mercator refinement tile series with one TileFrame per
	 * mosaic slot. Tiles are cut server-side from the same source frames as
	 * the mosaic, so a slot's tiles are always temporally consistent with
	 * the equirect texture the overlay shows for that slot; the mosaic acts
	 * as the base layer wherever tiles are missing or still loading.
	 */
	private updateTileSeries(records: RadarFrameRecord[]): void {
		const sortedRecords = [...records].reverse()
		const key = sortedRecords.map(r => r.slot_timestamp).join("|")
		if (key === this.lastTileSeriesKey) return
		this.lastTileSeriesKey = key

		const frames = sortedRecords.map(record => {
			// The archive key is the slot timestamp in exact ISO form
			// (radar/{ISO}.pmtiles, written by radar-services); the frame's
			// place in time is its observation time, matching the mosaic
			// texture frame for the same record
			const iso = new time(record.slot_timestamp).date.toISOString()
			return new TileFrame(
				`${cTileServerUrl}/radar/${iso}/{z}/{x}/{y}.webp`,
				new time(record.data_timestamp),
				timedelta.minutes(5)
			)
		})
		this.earth.cacheTask.wmTileSeries.setFrames(frames)
	}

	/**
	 * Update the frame cache with new records.
	 * - Reuses existing frames for URLs that are still valid
	 * - Creates new frames for new URLs
	 * - Purges frames for URLs no longer in the record set
	 */
	private updateFrameCache(records: RadarFrameRecord[]): RadarTextureFrame[] {
		// Records are sorted desc by slot_timestamp, we want chronological order
		const sortedRecords = [...records].reverse()

		// Build set of URLs from current records
		const currentUrls = new Set(sortedRecords.map(r => r.image_url))

		// Find and purge frames that are no longer needed
		const urlsToRemove: string[] = []
		for (const [url, frame] of this.frameCache) {
			if (!currentUrls.has(url)) {
				frame.purge()
				urlsToRemove.push(url)
			}
		}
		for (const url of urlsToRemove) {
			this.frameCache.delete(url)
		}

		// Build the frames array, reusing cached frames or creating new ones
		const frames: RadarTextureFrame[] = sortedRecords.map(record => {
			const existingFrame = this.frameCache.get(record.image_url)
			if (existingFrame) {
				return existingFrame
			}

			// Create new frame (global equirect, so wrap longitude at the
			// antimeridian instead of clamping), placed at its observation
			// time rather than the build slot
			const startTime = new time(record.data_timestamp)
			const duration = timedelta.minutes(5)
			const newFrame = new RadarTextureFrame(this.context, record.image_url, startTime, duration, true)
			this.frameCache.set(record.image_url, newFrame)
			return newFrame
		})

		return frames
	}

	/**
	 * Set visibility of the radar layer
	 */
	public setVisible(visible: boolean): void {
		if (this.visible === visible) return

		this.visible = visible

		if (visible) {
			this.earth.addOverlay(this.radarOverlay)
			// Repopulate the refinement tile series on next refresh
			this.lastTileSeriesKey = ""
			void this.refresh()
		} else {
			this.earth.removeOverlay(this.radarOverlay)
			this.lastTileSeriesKey = ""
			this.earth.cacheTask.clearWmTiles()
			// Reclaim GPU memory while hidden: purge the series (resets its
			// display state) plus any cached frames outside the series;
			// frames reload from the SQLite image cache when re-enabled
			this.radarOverlay.purge()
			for (const frame of this.frameCache.values()) {
				frame.purge()
			}
		}
	}

	override dispose(): void {
		// Unsubscribe from Redux state changes
		if (this.unsubscribe) {
			this.unsubscribe()
			this.unsubscribe = null
		}

		// Purge all cached frames
		for (const frame of this.frameCache.values()) {
			frame.purge()
		}
		this.frameCache.clear()

		this.earth.removeOverlay(this.radarOverlay)
		this.earth.cacheTask.clearWmTiles()
		super.dispose()
	}
}
